import { useCallback, useRef } from 'react';
import { FJDetailsPageAction } from '../../redux/action/FJDetailsPageAction';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import useLocalStore from '../../hooks/useLocalStore';
import FJDetailsPage from './FJDetailsPage';

export default function FJDetailsPageContainer() {
    useLocalStore();
    const dispatch = useAppDispatch();
    const items = useAppSelector(state => state.detailsPage.items);
    const selectedId = useAppSelector(state => state.detailsPage.selectedId);
    const selectedClipIds = useAppSelector(state => state.detailsPage.selectedClipIds);
    const projectTitle = useAppSelector(state => state.detailsPage.projectTitle);
    const mainRef = useRef<HTMLDivElement>(null);

    const handleSelect = useCallback(
        (id: string) => {
            const container = mainRef.current;
            if (!id || !container) {
                return;
            }
            const el = container.querySelector(`[data-details-id="${CSS.escape(id)}"]`);
            if (el instanceof HTMLElement) {
                const top =
                    el.getBoundingClientRect().top -
                    container.getBoundingClientRect().top +
                    container.scrollTop;
                container.scrollTo({ top, behavior: 'smooth' });
            }
            dispatch(FJDetailsPageAction.selectClipAction(id));
        },
        [dispatch],
    );

    const handleBack = useCallback(() => {
        dispatch(FJDetailsPageAction.hideDetailsPageAction());
    }, [dispatch]);

    const handleToggleLike = useCallback(
        (id: string) => {
            dispatch(FJDetailsPageAction.toggleClipLikedAction(id));
        },
        [dispatch],
    );

    const handleToggleDislike = useCallback(
        (id: string) => {
            dispatch(FJDetailsPageAction.toggleClipDislikedAction(id));
        },
        [dispatch],
    );

    const handleTryAgain = useCallback(
        (id: string) => {
            dispatch(FJDetailsPageAction.retryClipAction(id));
        },
        [dispatch],
    );

    const handlePublished = useCallback(
        (clipId: string) => {
            dispatch(FJDetailsPageAction.publishedClipAction(clipId));
        },
        [dispatch],
    );

    const handlePublishSocial = useCallback(
        (clipId: string) => {
            dispatch(FJDetailsPageAction.publishSocialClipAction(clipId));
        },
        [dispatch],
    );

    const handleCut = useCallback(
        (clipId: string, callback: () => void) => {
            // FJProjectStore.getEventId()
            dispatch(FJDetailsPageAction.cutClipAction(clipId, callback));
        },
        [dispatch],
    );

    const handleExportDownload = useCallback(
        (clipId: string, callback: () => void) => {
            dispatch(FJDetailsPageAction.handleDownloadAction(clipId, callback));
        },
        [dispatch],
    );

    const handleTrim = useCallback(
        (clipId: string) => {
            dispatch(FJDetailsPageAction.trimClipAction(clipId));
        },
        [dispatch],
    );

    const handleMoreRename = useCallback(
        (clipId: string) => {
            dispatch(FJDetailsPageAction.requestRenameClipAction(clipId));
        },
        [dispatch],
    );

    const handleMoreDelete = useCallback(
        (clipId: string) => {
            dispatch(FJDetailsPageAction.requestRemoveClipAction(clipId));
        },
        [dispatch],
    );

    const handleSetClipSelection = useCallback(
        (ids: Array<string | number>) => {
            dispatch(FJDetailsPageAction.setClipSelectionAction(ids.map(String)));
        },
        [dispatch],
    );

    const handleToggleClipSelection = useCallback(
        (id: string) => {
            dispatch(FJDetailsPageAction.toggleClipSelectionAction(id));
        },
        [dispatch],
    );

    const handleMultiDelete = useCallback(
        (ids: Array<string | number>) => {
            dispatch(FJDetailsPageAction.requestMultiRemoveClipsAction(ids.map(String)));
        },
        [dispatch],
    );

    const handleClearClipSelection = useCallback(() => {
        dispatch(FJDetailsPageAction.clearClipSelectionAction());
    }, [dispatch]);

    return (
        <FJDetailsPage
            items={items}
            selectedId={selectedId}
            selectedClipIds={selectedClipIds}
            mainRef={mainRef}
            onSelectItem={handleSelect}
            onBack={handleBack}
            onToggleLike={handleToggleLike}
            onToggleDislike={handleToggleDislike}
            onTryAgain={handleTryAgain}
            sidebarProjectTitle={projectTitle}
            onPublished={handlePublished}
            onPublishSocial={handlePublishSocial}
            onExportDownload={handleExportDownload}
            onTrim={handleTrim}
            onCut={handleCut}
            onMoreRename={handleMoreRename}
            onMoreDelete={handleMoreDelete}
            onSetClipSelection={handleSetClipSelection}
            onToggleClipSelection={handleToggleClipSelection}
            onMultiDelete={handleMultiDelete}
            onClearClipSelection={handleClearClipSelection}
        />
    );
}
